/**
 * ThreeTimelineComposition — GPU-accelerated video compositor using Three.js/R3F.
 *
 * Architecture:
 *   Layer 0 (hidden DOM): Remotion <Video>/<Img> elements for playback + audio
 *   Layer 1 (ThreeCanvas): R3F scene capturing DOM textures + postprocessing effects
 *   Layer 2 (DOM): Text overlays (CSS-rendered, above ThreeCanvas)
 *   Layer 3 (DOM): Extra audio tracks
 *
 * Effects pipeline: @react-three/postprocessing (GPU) replaces CSS filters.
 * Transitions: rendered as shader blends between two clip textures.
 * Text: stays DOM/CSS (correct tool for rich text animation).
 */

import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, delayRender, continueRender, useRemotionEnvironment } from 'remotion'
import { useMemo, useRef, useEffect } from 'react'
import { ThreeCanvas } from '@remotion/three'
import { useThree, useFrame } from '@react-three/fiber'
import {
  EffectComposer,
  ChromaticAberration,
  Vignette,
  Bloom,
  Scanline,
} from '@react-three/postprocessing'
import { KernelSize } from 'postprocessing'
import * as THREE from 'three'

import {
  buildTimelineModel,
  getActiveClips,
  COLOR_GRADE_FILTERS,
  type TimelineClip,
  type ActiveClipInfo,
  type TransitionInfo,
} from '@/lib/clipUtils'
import { ClipRenderer } from './components/ClipRenderer'
import { DemoSceneRenderer } from './demo/DemoSceneRenderer'
import { TextOverlayRenderer } from './components/TextOverlayRenderer'
import { ClipAudioRenderer, ExtraAudioRenderer } from './components/AudioRenderer'
import type { CompositionProps } from './types'
import { secondsToFrames, framesToSeconds, rangeToFrames } from './utils/framemath'
import { KeyframeEvaluator, type KeyframeTracks } from '@/lib/keyframes'
import { getGLTransition } from './utils/glTransitionRegistry'
import { compileTransitionProgram, drawFrame, dispose, createGLContext, type GLProgram } from './utils/webglHelper'
import { buildColorMatrix, isIdentityMatrix } from './utils/cssColorMatrix'
import { CSSColorMatrixEffect } from './effects/CSSColorMatrixEffect'

// No handoff overlap or premount — clips play audio in their exact time window only.
// Preloading is handled by blob-url prefetch in VideoPreview, not by component premounting.

const PREVIEW_ACCEPTABLE_MEDIA_TIMESHIFT_SECONDS = 10

// ---------------------------------------------------------------------------
// Orthographic camera that fills the composition dimensions
// ---------------------------------------------------------------------------
function OrthoSetup({ width, height }: { width: number; height: number }) {
  const { camera } = useThree()
  useEffect(() => {
    if (camera instanceof THREE.OrthographicCamera) {
      camera.left = -width / 2
      camera.right = width / 2
      camera.top = height / 2
      camera.bottom = -height / 2
      camera.position.set(0, 0, 1)
      camera.lookAt(0, 0, 0)
      camera.updateProjectionMatrix()
    }
  }, [camera, width, height])
  return null
}

// ---------------------------------------------------------------------------
// Shared source-finding utility — reads from clipFrameStore (populated by
// ClipRenderer's onVideoFrame callback). This is the SINGLE codepath for both
// preview and export — no DOM querying, no element-type branching.
// ---------------------------------------------------------------------------
import { getClipFrame } from './clipFrameStore'

interface ClipSource {
  element: TexImageSource
  sourceWidth: number
  sourceHeight: number
}

function findClipSource(clipId: string): ClipSource | null {
  const frame = getClipFrame(clipId)
  if (!frame) return null
  return { element: frame.source as TexImageSource, sourceWidth: frame.width, sourceHeight: frame.height }
}

// Compute cover-crop UV bounds for mapping a source aspect ratio into a
// destination aspect ratio. Returns the UV sub-rectangle to sample from
// the source texture so it fills the destination without stretching.
function computeCoverUVs(srcW: number, srcH: number, dstW: number, dstH: number) {
  const srcAspect = srcW / srcH
  const dstAspect = dstW / dstH

  let uMin = 0, uMax = 1, vMin = 0, vMax = 1
  if (srcAspect > dstAspect) {
    const scale = dstAspect / srcAspect
    const offset = (1 - scale) / 2
    uMin = offset
    uMax = 1 - offset
  } else if (srcAspect < dstAspect) {
    const scale = srcAspect / dstAspect
    const offset = (1 - scale) / 2
    vMin = offset
    vMax = 1 - offset
  }
  return { uMin, uMax, vMin, vMax }
}

// ---------------------------------------------------------------------------
// Clip plane: captures DOM element → Three.js texture → renders on a plane
// ---------------------------------------------------------------------------
function ClipPlane({
  clipId,
  clip,
  alpha,
  width,
  height,
  zOffset,
  fps,
  localTime,
}: {
  clipId: string
  clip: TimelineClip
  alpha: number
  width: number
  height: number
  zOffset: number
  fps: number
  localTime: number
}) {
  const frame = useCurrentFrame()
  const { isRendering } = useRemotionEnvironment()
  const { advance } = useThree()
  const isSolidColor = !!clip.solidColor
  // Pre-create a placeholder texture at mount so the meshBasicMaterial's shader
  // compiles with the MAP define from the very first render. Without this, the
  // first time we attach a texture the shader has to recompile (no MAP → MAP),
  // and Three.js renders one empty frame during the recompile — captured as a
  // black frame in export. Solid-color clips don't need a texture.
  const textureRef = useRef<THREE.Texture | null>(null)
  if (!isSolidColor && !textureRef.current) {
    const tex = new THREE.Texture()
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter
    tex.colorSpace = THREE.SRGBColorSpace
    textureRef.current = tex
  }
  const materialRef = useRef<THREE.MeshBasicMaterial>(null)
  const geometryRef = useRef<THREE.PlaneGeometry>(null)

  // Dispose texture on unmount to free GPU memory.
  useEffect(() => {
    return () => {
      textureRef.current?.dispose()
      textureRef.current = null
    }
  }, [])

  const localFrame = Math.round(localTime * fps)

  // Keyframe-driven transforms
  const kfEvaluator = useMemo(() => {
    if (!clip.effects?.animationKeyframes) return null
    return new KeyframeEvaluator(clip.effects.animationKeyframes as KeyframeTracks)
  }, [clip.effects?.animationKeyframes])

  const localTimeSec = localTime

  // Apply cover UV mapping to the geometry so the source texture fills the
  // composition without stretching. Uses explicit dimensions from findClipSource.
  const lastCoverKeyRef = useRef('')
  const applyCoverUVs = (srcW: number, srcH: number) => {
    const geom = geometryRef.current
    if (!geom || !srcW || !srcH) return

    const key = `${srcW}x${srcH}>${width}x${height}`
    if (key === lastCoverKeyRef.current) return
    lastCoverKeyRef.current = key

    const { uMin, uMax, vMin, vMax } = computeCoverUVs(srcW, srcH, width, height)

    const uv = geom.attributes.uv as THREE.BufferAttribute
    uv.setXY(0, uMin, vMax)
    uv.setXY(1, uMax, vMax)
    uv.setXY(2, uMin, vMin)
    uv.setXY(3, uMax, vMin)
    uv.needsUpdate = true
  }

  // Swap the placeholder Texture for a VideoTexture the first time a video
  // source arrives. VideoTexture auto-uploads pixels each render — required for
  // paused preview to show the current frame, since useFrame stops firing on
  // pause. The swap doesn't change shader defines (MAP stays defined either
  // way), so Three.js reuses the cached program — no recompile, no black frame.
  const updateTexture = (source: ClipSource) => {
    const current = textureRef.current
    if (!current) return

    const isVideoSource = source.element instanceof HTMLVideoElement
    const isVideoTexture = current instanceof THREE.VideoTexture

    if (isVideoSource && !isVideoTexture) {
      const videoTex = new THREE.VideoTexture(source.element as HTMLVideoElement)
      videoTex.minFilter = THREE.LinearFilter
      videoTex.magFilter = THREE.LinearFilter
      videoTex.colorSpace = THREE.SRGBColorSpace
      current.dispose()
      textureRef.current = videoTex
      if (materialRef.current) {
        materialRef.current.map = videoTex
      }
    } else if (isVideoSource && isVideoTexture) {
      if (current.image !== source.element) {
        current.image = source.element
      }
    } else {
      current.image = source.element
      current.needsUpdate = true
    }

    applyCoverUVs(source.sourceWidth, source.sourceHeight)
  }

  // Texture capture: poll until DOM source is ready, then update + re-render.
  // In rendering mode, delayRender keeps Remotion waiting for the texture.
  // In preview mode, this handles initial load / pause where useFrame won't
  // fire again until the next frame change.
  // Solid color clips skip texture polling entirely — no DOM source to capture.
  useEffect(() => {
    if (isSolidColor) return
    const handle = isRendering ? delayRender(`ClipPlane ${clipId} frame ${frame}`, { timeoutInMilliseconds: 240000 }) : null
    let cancelled = false
    let resolved = false
    const startTime = Date.now()
    const tryCapture = () => {
      if (cancelled) return
      const source = findClipSource(clipId)
      if (!source) {
        // In rendering mode, give up after 30s and render with what we have
        // (black or last texture) instead of blocking the entire render.
        if (isRendering && Date.now() - startTime > 30_000) {
          console.warn(`[ClipPlane] Timeout waiting for frame store: ${clipId} frame ${frame}, continuing without texture`)
          advance(performance.now())
          resolved = true
          if (handle != null) continueRender(handle)
          return
        }
        // In rendering mode (Lambda), use setTimeout instead of
        // requestAnimationFrame — RAF can deadlock with Remotion's render
        // loop since Remotion pauses RAF while waiting for delayRender.
        if (isRendering) { setTimeout(tryCapture, 10) } else { requestAnimationFrame(tryCapture) }
        return
      }
      updateTexture(source)
      advance(performance.now())
      resolved = true
      if (handle != null) continueRender(handle)
    }

    if (isRendering) { setTimeout(tryCapture, 0) } else { requestAnimationFrame(tryCapture) }
    return () => {
      cancelled = true
      // Only resolve the handle on cleanup if tryCapture never did — prevents
      // orphaned delayRender handles from blocking the frame forever.
      if (!resolved && handle != null) continueRender(handle)
    }
  }, [frame, clipId, isRendering, advance, isSolidColor])

  // PREVIEW: useFrame runs during advance() — continuously captures the
  // latest video frame while playing. Priority 0 fires before EffectComposer.
  useFrame(() => {
    if (isRendering || isSolidColor) return
    const source = findClipSource(clipId)
    if (source) updateTexture(source)
  })

  // Spatial positioning: percentage coords → Three.js orthographic space
  const spatialFracW = (clip.spatialWidth ?? 100) / 100
  const spatialFracH = (clip.spatialHeight ?? 100) / 100
  const spatialFracX = (clip.spatialX ?? 0) / 100
  const spatialFracY = (clip.spatialY ?? 0) / 100
  const planeW = spatialFracW * width
  const planeH = spatialFracH * height
  const spatialPosX = width * (-0.5 + spatialFracX + spatialFracW / 2)
  const spatialPosY = height * (0.5 - spatialFracY - spatialFracH / 2)
  const spatialRot = clip.spatialRotation ? -(clip.spatialRotation * Math.PI / 180) : 0

  // Compute transforms: shake, ken burns, keyframe overrides
  let transform = ''
  const effects = clip.effects

  if (effects?.shake && effects.shake > 0) {
    const sx = Math.sin(frame * 1.7) * 4 * effects.shake
    const sy = Math.cos(frame * 2.3) * 3 * effects.shake
    transform += `translate(${sx}px, ${sy}px) `
  }

  // Ken Burns as mesh position/scale
  let scale = 1
  let posX = spatialPosX
  let posY = spatialPosY

  if (effects?.kenBurns) {
    const kb = effects.kenBurns
    const clipDurationFrames = secondsToFrames(clip.duration, fps)
    const progress = Math.min(1, localFrame / clipDurationFrames)
    scale = kb.startScale + (kb.endScale - kb.startScale) * progress
    posX = ((kb.startX ?? 0) + ((kb.endX ?? 0) - (kb.startX ?? 0)) * progress) * width * 0.5
    posY = ((kb.startY ?? 0) + ((kb.endY ?? 0) - (kb.startY ?? 0)) * progress) * height * 0.5
  }

  // Keyframe transform overrides
  if (kfEvaluator) {
    const kfScale = kfEvaluator.evaluate('scale', localTimeSec)
    if (kfScale !== undefined) scale *= kfScale
    const kfX = kfEvaluator.evaluate('x', localTimeSec)
    if (kfX !== undefined) posX += kfX * width * 0.01
    const kfY = kfEvaluator.evaluate('y', localTimeSec)
    if (kfY !== undefined) posY += kfY * height * 0.01
    const kfRotation = kfEvaluator.evaluate('rotation', localTimeSec)
    if (kfRotation !== undefined) transform += `rotate(${kfRotation}deg) `
  }

  const kfOpacity = kfEvaluator?.evaluate('opacity', localTimeSec)
  const finalOpacity = (kfOpacity ?? 1) * alpha * (clip.spatialOpacity ?? 1)

  return (
    <mesh
      position={[posX, posY, -zOffset * 0.01]}
      scale={[scale * planeW, scale * planeH, 1]}
      rotation={spatialRot ? [0, 0, spatialRot] : undefined}
    >
      <planeGeometry ref={geometryRef} args={[1, 1]} />
      <meshBasicMaterial
        ref={materialRef}
        map={isSolidColor ? null : textureRef.current}
        color={isSolidColor ? clip.solidColor! : '#ffffff'}
        transparent={finalOpacity < 1}
        opacity={finalOpacity}
        toneMapped={false}
        key={isSolidColor ? 'solid' : 'tex'}
      />
    </mesh>
  )
}

// ---------------------------------------------------------------------------
// GL Transition plane: renders transition between two DOM-captured clips
// ---------------------------------------------------------------------------
function GLTransitionPlane({
  outClipId,
  inClipId,
  transitionName,
  progress,
  width,
  height,
}: {
  outClipId: string
  inClipId: string
  transitionName: string
  progress: number
  width: number
  height: number
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const glRef = useRef<WebGLRenderingContext | null>(null)
  const progRef = useRef<GLProgram | null>(null)
  const textureRef = useRef<THREE.CanvasTexture | null>(null)
  const frame = useCurrentFrame()

  const transition = useMemo(() => getGLTransition(transitionName), [transitionName])

  // Create offscreen canvas + GL context
  useEffect(() => {
    if (!transition) return
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    canvasRef.current = canvas

    const gl = createGLContext(canvas)
    if (!gl) return
    try {
      const prog = compileTransitionProgram(gl, transition.glsl, transition.defaultParams)
      glRef.current = gl
      progRef.current = prog
      textureRef.current = new THREE.CanvasTexture(canvas)
      textureRef.current.minFilter = THREE.LinearFilter
      textureRef.current.magFilter = THREE.LinearFilter
    } catch {
      glRef.current = null
      progRef.current = null
    }

    return () => {
      if (glRef.current && progRef.current) {
        dispose(glRef.current, progRef.current)
      }
      textureRef.current?.dispose()
      glRef.current = null
      progRef.current = null
      canvasRef.current = null
    }
  }, [transition, width, height])

  // Draw transition each frame — uses shared findClipSource for consistency
  useEffect(() => {
    const gl = glRef.current
    const prog = progRef.current
    if (!gl || !prog) return

    const fromSource = findClipSource(outClipId)
    const toSource = findClipSource(inClipId)
    if (fromSource && toSource) {
      try {
        drawFrame(gl, prog, fromSource.element, toSource.element, progress)
        if (textureRef.current) textureRef.current.needsUpdate = true
      } catch { /* WebGL error */ }
    }
  }, [frame, outClipId, inClipId, progress])

  if (!textureRef.current || !transition) return null

  return (
    <mesh position={[0, 0, 0.01]}>
      <planeGeometry args={[width, height]} />
      <meshBasicMaterial map={textureRef.current} toneMapped={false} />
    </mesh>
  )
}

// ---------------------------------------------------------------------------
// CSS-style transition fallback (crossfade, wipe, dip) — handled via clip alpha
// ---------------------------------------------------------------------------
function isCSSTransition(type: string): boolean {
  return ['crossfade', 'wipe_left', 'wipe_right', 'dip_to_black', 'dip_to_white'].includes(type)
}

// ---------------------------------------------------------------------------
// Postprocessing effects — reads from current clip's effects object
// ---------------------------------------------------------------------------
function CSSColorMatrixPrimitive({ effect }: { effect: CSSColorMatrixEffect }) {
  return <primitive object={effect} dispose={null} />
}

function PostEffects({ activeClips }: { activeClips: ActiveClipInfo[] }) {
  const currentClip = activeClips[0]?.entry.clip
  const effects = currentClip?.effects

  // Keyframe evaluator for animated effects
  const kfEvaluator = useMemo(() => {
    if (!effects?.animationKeyframes) return null
    return new KeyframeEvaluator(effects.animationKeyframes as KeyframeTracks)
  }, [effects?.animationKeyframes])

  const localTimeSec = activeClips[0]?.localTime ?? 0

  // Resolve effect values (with keyframe overrides) — CSS scale (1 = no change)
  const brightness = kfEvaluator?.evaluate('brightness', localTimeSec) ?? effects?.brightness ?? 1
  const contrast = kfEvaluator?.evaluate('contrast', localTimeSec) ?? effects?.contrast ?? 1
  const saturation = kfEvaluator?.evaluate('saturation', localTimeSec) ?? effects?.saturation ?? 1
  const hueRotateDeg = effects?.hueRotate ?? 0
  const vignette = kfEvaluator?.evaluate('vignette', localTimeSec) ?? effects?.vignette ?? 0
  const ca = kfEvaluator?.evaluate('chromaticAberration', localTimeSec) ?? effects?.chromaticAberration ?? 0
  const bloomVal = kfEvaluator?.evaluate('bloom', localTimeSec) ?? effects?.bloom ?? 0
  const colorGrade = effects?.colorGrade
  const gradeIntensity = effects?.colorGradeIntensity ?? 1

  const gradeFilterStr = colorGrade ? COLOR_GRADE_FILTERS[colorGrade] : undefined

  // Build a single CSS-spec-accurate color matrix for grade + user adjustments
  const colorMatrix = useMemo(
    () => buildColorMatrix(gradeFilterStr, gradeIntensity, brightness, contrast, saturation, hueRotateDeg),
    [gradeFilterStr, gradeIntensity, brightness, contrast, saturation, hueRotateDeg],
  )

  const hasColorMatrix = !isIdentityMatrix(colorMatrix)

  // Persistent Effect instance — update uniforms without recreating the object
  const colorMatrixEffect = useMemo(() => new CSSColorMatrixEffect(colorMatrix), [])
  useEffect(() => { colorMatrixEffect.setColorMatrix(colorMatrix) }, [colorMatrix, colorMatrixEffect])

  const caOffset = useMemo(() => new THREE.Vector2(ca * 0.006, ca * 0.006), [ca])

  const hasEffects =
    hasColorMatrix ||
    vignette > 0 || ca > 0 || bloomVal > 0 || effects?.vhs

  if (!hasEffects) return null

  const effectList: React.ReactElement[] = []
  if (hasColorMatrix) effectList.push(<CSSColorMatrixPrimitive key="colorMatrix" effect={colorMatrixEffect} />)
  if (ca > 0) effectList.push(<ChromaticAberration key="ca" offset={caOffset} radialModulation={false} modulationOffset={0} />)
  if (vignette > 0) effectList.push(<Vignette key="vig" darkness={vignette} offset={0.3} />)
  if (bloomVal > 0) effectList.push(<Bloom key="bloom" intensity={bloomVal * 2} luminanceThreshold={0.3} luminanceSmoothing={0.4} kernelSize={KernelSize.LARGE} />)
  if (effects?.vhs) {
    effectList.push(<Scanline key="vhs-scan" density={1.5} opacity={0.15} />)
  }

  return (
    <EffectComposer multisampling={0} depthBuffer={false} enableNormalPass={false}>
      {effectList}
    </EffectComposer>
  )
}

// ---------------------------------------------------------------------------
// Fade in/out overlays (rendered as colored planes)
// ---------------------------------------------------------------------------
function FadeOverlays({ clip, fps, width, height, localTime }: { clip: TimelineClip; fps: number; width: number; height: number; localTime: number }) {
  const localFrame = Math.round(localTime * fps)
  const effects = clip.effects
  if (!effects) return null

  const nodes: React.ReactNode[] = []

  if (effects.fadeIn) {
    const fadeDurationFrames = secondsToFrames(effects.fadeIn.duration, fps)
    if (localFrame < fadeDurationFrames) {
      const opacity = 1 - localFrame / fadeDurationFrames
      const color = effects.fadeIn.color === 'white' ? '#ffffff' : '#000000'
      nodes.push(
        <mesh key="fadeIn" position={[0, 0, 0.05]}>
          <planeGeometry args={[width * 2, height * 2]} />
          <meshBasicMaterial color={color} transparent opacity={opacity} toneMapped={false} />
        </mesh>
      )
    }
  }

  if (effects.fadeOut) {
    const clipDurationFrames = secondsToFrames(clip.duration, fps)
    const fadeDurationFrames = secondsToFrames(effects.fadeOut.duration, fps)
    const fadeStartFrame = clipDurationFrames - fadeDurationFrames
    if (localFrame >= fadeStartFrame) {
      const opacity = (localFrame - fadeStartFrame) / fadeDurationFrames
      const color = effects.fadeOut.color === 'white' ? '#ffffff' : '#000000'
      nodes.push(
        <mesh key="fadeOut" position={[0, 0, 0.05]}>
          <planeGeometry args={[width * 2, height * 2]} />
          <meshBasicMaterial color={color} transparent opacity={opacity} toneMapped={false} />
        </mesh>
      )
    }
  }

  // Letterbox bars — position in camera space (ortho camera: -height/2 to +height/2)
  if (effects.letterbox) {
    const barFraction = (1 - 16 / 9 / 2.39) / 2
    const barH = height * barFraction
    nodes.push(
      <mesh key="letterbox-top" position={[0, (height - barH) / 2, 0.06]}>
        <planeGeometry args={[width * 2, barH]} />
        <meshBasicMaterial color="#000000" toneMapped={false} />
      </mesh>,
      <mesh key="letterbox-bottom" position={[0, -(height - barH) / 2, 0.06]}>
        <planeGeometry args={[width * 2, barH]} />
        <meshBasicMaterial color="#000000" toneMapped={false} />
      </mesh>
    )
  }

  return <>{nodes}</>
}

// ---------------------------------------------------------------------------
// Compositor scene: orchestrates clip planes + effects
// ---------------------------------------------------------------------------
function CompositorScene({
  activeClips,
  transition,
  width,
  height,
  fps,
}: {
  activeClips: ActiveClipInfo[]
  transition?: TransitionInfo
  width: number
  height: number
  fps: number
}) {

  // CSS transitions: alpha-blend two clip planes
  // GL transitions: render transition in a separate plane on top
  const isGL = transition && !isCSSTransition(transition.type)

  return (
    <>
      <color attach="background" args={['#000000']} />
      <OrthoSetup width={width} height={height} />

      {/* Clip planes */}
      {activeClips.map((ac, i) => (
        <ClipPlane
          key={ac.entry.clip._id}
          clipId={ac.entry.clip._id}
          clip={ac.entry.clip}
          alpha={isGL ? (i === 0 ? 1 - transition!.progress : 0) : ac.alpha}
          width={width}
          height={height}
          zOffset={activeClips.length - i}
          fps={fps}
          localTime={ac.localTime}
        />
      ))}

      {/* GL transition overlay */}
      {isGL && activeClips.length >= 2 && (
        <GLTransitionPlane
          outClipId={activeClips[0].entry.clip._id}
          inClipId={activeClips[1].entry.clip._id}
          transitionName={transition!.type}
          progress={transition!.progress}
          width={width}
          height={height}
        />
      )}

      {/* Fade in/out + letterbox overlays for current clip */}
      {activeClips[0] && (
        <FadeOverlays clip={activeClips[0].entry.clip} fps={fps} width={width} height={height} localTime={activeClips[0].localTime} />
      )}

      {/* GPU postprocessing effects */}
      <PostEffects activeClips={activeClips} />
    </>
  )
}

// ---------------------------------------------------------------------------
// Main composition
// ---------------------------------------------------------------------------
export function ThreeTimelineComposition({
  clips,
  textOverlays = [],
  audioTracks = [],
  audioMix,
  timelineMode,
  demoStyle,
}: CompositionProps) {
  const { fps, width, height } = useVideoConfig()
  const frame = useCurrentFrame()
  const { isRendering } = useRemotionEnvironment()

  const model = useMemo(
    () => buildTimelineModel(clips, { mode: timelineMode }),
    [clips, timelineMode],
  )

  const currentTimeSec = framesToSeconds(frame, fps)
  const { clips: activeClips, transition } = useMemo(
    () => getActiveClips(model, currentTimeSec),
    [model, currentTimeSec],
  )

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      {/* Layer 0: hidden DOM clips used only as frame sources for the canvas.
          In preview, keep them muted and timeline-bounded so a visual stall
          cannot perturb the Player's global audio clock. */}
      <div style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}>
        {model.entries.map((entry) => {
          const startFrame = secondsToFrames(entry.globalStart, fps)
          const durationFrames = rangeToFrames(entry.globalStart, entry.globalEnd, fps).durationFrames
          const premountFrames = isRendering ? undefined : Math.min(startFrame, fps * 3)
          return (
            <Sequence
              key={`dom-${entry.clip._id}`}
              from={startFrame}
              durationInFrames={durationFrames}
              premountFor={premountFrames}
            >
              <div data-clip-id={entry.clip._id} style={{ position: 'absolute', inset: 0 }}>
                <ClipRenderer
                  clip={entry.clip}
                  fps={fps}
                  volume={0}
                  muted
                  pauseWhenBuffering={false}
                  acceptableTimeShiftInSeconds={PREVIEW_ACCEPTABLE_MEDIA_TIMESHIFT_SECONDS}
                />
              </div>
            </Sequence>
          )
        })}
      </div>

      {/* Layer 1: Three.js GPU compositor */}
      <ThreeCanvas
        width={width}
        height={height}
        orthographic
        gl={{ preserveDrawingBuffer: true, alpha: false }}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: '#000',
          filter: (activeClips[0]?.entry.clip?.effects?.blur ?? 0) > 0
            ? `blur(${activeClips[0].entry.clip.effects!.blur}px)`
            : undefined,
        }}
      >
        <CompositorScene
          activeClips={activeClips}
          transition={transition}
          width={width}
          height={height}
          fps={fps}
        />
      </ThreeCanvas>

      {/* Layer 1.5: product-demo scenes (DOM, above ThreeCanvas). Demo clips
          render synthetically — screenshots, camera moves, cursor — so they
          bypass the GL video compositor entirely. */}
      {model.entries.some((e) => e.clip.demoScene) ? (
        <AbsoluteFill style={{ pointerEvents: 'none' }}>
          {model.entries
            .filter((e) => e.clip.demoScene)
            .map((entry) => {
              const startFrame = secondsToFrames(entry.globalStart, fps)
              const durationFrames = rangeToFrames(entry.globalStart, entry.globalEnd, fps).durationFrames
              return (
                <Sequence key={`demo-${entry.clip._id}`} from={startFrame} durationInFrames={durationFrames}>
                  <DemoSceneRenderer
                    scene={entry.clip.demoScene!}
                    demoStyle={demoStyle}
                    durationInFrames={durationFrames}
                    globalStartSec={entry.globalStart}
                  />
                </Sequence>
              )
            })}
        </AbsoluteFill>
      ) : null}

      {/* Layer 2: text overlays (DOM, above ThreeCanvas) */}
      <AbsoluteFill style={{ pointerEvents: 'none' }}>
        <TextOverlayRenderer overlays={textOverlays} />
      </AbsoluteFill>

      {/* Layer 3: audio playback, independent from the hidden frame-source videos */}
      <ClipAudioRenderer
        model={model}
        audioMix={audioMix}
        pauseWhenBuffering={false}
        acceptableTimeShiftInSeconds={PREVIEW_ACCEPTABLE_MEDIA_TIMESHIFT_SECONDS}
      />
      <ExtraAudioRenderer
        audioTracks={audioTracks}
        audioMix={audioMix}
        pauseWhenBuffering={false}
        acceptableTimeShiftInSeconds={PREVIEW_ACCEPTABLE_MEDIA_TIMESHIFT_SECONDS}
      />
    </AbsoluteFill>
  )
}
